import { type FC } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import {
  FuelTypeLabel,
  RatingUnitLabel,
  SourceLabel,
  SystemLabel,
} from '#apps/client-web/features/audit-form-mechanical/constants';
import { floatToFraction } from '#apps/client-web/features/audit-form-mechanical/helpers';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input';

import { type System } from '#packages/data-collection/models/common/system';
import { type Source } from '#packages/data-collection/models/common/sources';
import {
  type FuelType,
  type RatingUnit,
} from '#packages/data-collection/models/audit-mechanical-form';

import { type EditAuditFormMechanicalEntry } from './types';

export type AuditFormMechanicalEntryPageRendererProps = {
  changeRatingUnit: (value: RatingUnit | undefined) => void;
  form: EditAuditFormMechanicalEntry;
  isPending: boolean;
  onSubmit: () => void;
};

export const AuditFormMechanicalEntryPageRenderer: FC<
  AuditFormMechanicalEntryPageRendererProps
> = ({ form, isPending, changeRatingUnit, onSubmit }) => {
  const { control } = form;
  const disableForm = isPending;
  const ratingUnit = useWatch({
    control,
    name: 'ratingUnit',
  });

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={4} sx={{ mt: 2 }} flex={1}>
        <Controller
          control={control}
          name="system"
          disabled={disableForm}
          rules={{
            required: { value: true, message: 'System is required' },
          }}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                getOptionLabel={(option) => SystemLabel[option as System]}
                options={Object.keys(SystemLabel)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="System"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="location"
              disabled={disableForm}
              rules={{
                required: { value: true, message: 'Location is required' },
                minLength: 1,
                maxLength: 253,
              }}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Location"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="serviceAreaOrEquipment"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Service area or equipment"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="type"
              disabled={disableForm}
              rules={{
                required: { value: true, message: 'Type is required' },
                minLength: 1,
                maxLength: 253,
              }}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Type"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Controller
              control={control}
              name="tag"
              disabled={disableForm}
              rules={{
                required: { value: true, message: 'Tag is required' },
                minLength: 1,
                maxLength: 253,
              }}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Tag"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Controller
              control={control}
              name="quantity"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Quantity"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="make"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Make"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="model"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Model"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="powerRating"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    slotProps={{
                      input: {
                        endAdornment: ratingUnit === 'hp' && (
                          <InputAdornment position="end">
                            {floatToFraction(field.value ?? '')}
                          </InputAdornment>
                        ),
                      },
                    }}
                    label="Power Rating"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="ratingUnit"
              disabled={disableForm}
              render={({ field, fieldState }) => {
                return (
                  <FormControl
                    fullWidth
                    {...getFormErrorAttributes(fieldState.error)}>
                    <Autocomplete
                      {...field}
                      {...getFormErrorAttributes(fieldState.error)}
                      options={Object.keys(RatingUnitLabel)}
                      getOptionLabel={(option) =>
                        RatingUnitLabel[option as RatingUnit]
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Rating Unit"
                          variant="outlined"
                        />
                      )}
                      value={field.value ?? null}
                      onChange={(_, value) =>
                        changeRatingUnit(value as RatingUnit)
                      }
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Controller
              control={control}
              name="ratingSource"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    freeSolo
                    getOptionLabel={(option) =>
                      SourceLabel[option as Source] ?? ''
                    }
                    includeInputInList
                    options={Object.keys(SourceLabel)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Rating Source"
                        variant="outlined"
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Controller
          control={control}
          name="fuelType"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                getOptionLabel={(option) => FuelTypeLabel[option as FuelType]}
                options={Object.keys(FuelTypeLabel)}
                renderInput={(params) => (
                  <TextField {...params} label="Fuel Type" variant="outlined" />
                )}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="efficiency"
              disabled={disableForm}
              rules={{
                min: {
                  value: 0,
                  message: 'Efficiency should be between 0 and 100',
                },
                max: {
                  value: 100,
                  message: 'Efficiency should be between 0 and 100',
                },
              }}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      },
                    }}
                    label="Efficiency"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="efficiencySource"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    freeSolo
                    getOptionLabel={(option) =>
                      SourceLabel[option as Source] ?? ''
                    }
                    includeInputInList
                    options={Object.keys(SourceLabel)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Efficiency Source"
                        variant="outlined"
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Controller
          control={control}
          name="ventilationRate"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Ventilation Rate (CFM)"
                type="number"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Controller
              control={control}
              name="serialNumber"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Serial Number"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Controller
              control={control}
              name="manufactureDate"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    label="Manufacture Date"
                    type="date"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        <Controller
          control={control}
          name="hours"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Hours"
                type="number"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="control"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Control"
                type="text"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="other"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Other"
                type="text"
                multiline
                rows={5}
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="attachments"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <FileInput
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Attachments"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Stack>
    </Box>
  );
};
